import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDebounce } from "@/hooks/useDebounce";
import {
  fetchProducts,
  fetchRefrigerants,
  fetchConfig,
  calculate as calculateApi,
  buildVPF,
} from "@/lib/api";
import {
  FALLBACK_INPUTS, coerceCalcInputs, localErrors, type LocalErrorContext,
} from "@/lib/validators";
import { applyOnBlurWithNote, stripOverride } from "@/lib/autocorrect";
import type {
  CalcInputs,
  CalcResult,
  CalcStatus,
  ProductProperty,
  Refrigerant,
  VPFDimensions,
} from "@/types/calc";

interface Option {
  value: string;
  label: string;
}

/** The AUTO/MAN override fields. */
export type ManualKey = "overrideFreezeTime" | "overrideDefrostMin";
export type ManualFlags = Record<ManualKey, boolean>;
const MANUAL_KEYS: ManualKey[] = ["overrideFreezeTime", "overrideDefrostMin"];
export const MAN_EMPTY_MSG = "Enter a value or switch back to AUTO";

/** MAN flags derived from stored values (non-empty override = MAN). */
const manualFrom = (inp: CalcInputs): ManualFlags => ({
  overrideFreezeTime: (inp.overrideFreezeTime ?? "").trim() !== "",
  overrideDefrostMin: (inp.overrideDefrostMin ?? "").trim() !== "",
});

export interface UseCalculator {
  inputs: CalcInputs;
  set: (key: keyof CalcInputs) => (value: string) => void;
  blur: (key: keyof CalcInputs) => () => void;
  /** Autocorrect notes from the last blur ("40 → 35 (max)…"), cleared on the next edit. */
  notes: Partial<Record<keyof CalcInputs, string>>;
  /** AUTO/MAN mode of the override fields — only the pill changes it, so
   *  clearing a MAN field never silently flips it back to AUTO. */
  manual: ManualFlags;
  setManual: (key: ManualKey, on: boolean, prefill?: string) => void;
  resetInputs: (partial: Partial<CalcInputs>) => void;
  clearInputs: () => void;
  products: ProductProperty[];
  refrigerants: Refrigerant[];
  productOptions: Option[];
  refrigerantOptions: Option[];
  primaryRefrigerantOptions: Option[];
  heatTransferFluidOptions: Option[];
  htfOutletTemp: number | null;
  result: CalcResult | null;
  vpfDimensions: VPFDimensions | null;
  status: CalcStatus;
  /** Results on screen no longer match the inputs (invalid input, or the
   *  recalculation after an invalid state has not landed yet). */
  stale: boolean;
  banner: string[];
  /** Set when /products, /refrigerants or /config failed to load. */
  refDataError: string | null;
  fieldErrors: Record<string, string>;
}

// ── localStorage persistence ──────────────────────────────────────────────────
const STORAGE_KEY = "jackstone-vpf-inputs-v1";

/** Load inputs from localStorage, merging with FALLBACK_INPUTS so new fields
 *  added in future updates always get their defaults. */
function loadSavedInputs(): CalcInputs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    // Coerced key by key — a corrupt / hand-edited entry can't crash the page
    if (raw) return { ...FALLBACK_INPUTS, ...coerceCalcInputs(JSON.parse(raw)) };
  } catch {
    /* private-browsing / parse error — fall through */
  }
  return FALLBACK_INPUTS;
}

const hasStoredInputs = (() => {
  try { return !!localStorage.getItem(STORAGE_KEY); }
  catch { return false; }
})();

/** Client-side context for cross-field rules — the selected product's
 *  freezing point comes from the /products reference data. */
function errorContext(inputs: CalcInputs, products: ProductProperty[]): LocalErrorContext {
  const product = products.find((p) => p.name === inputs.thermalProperty);
  return { productTFus: product ? product.TFus : null };
}

/** localErrors plus "MAN selected but empty" for the override fields. */
function allErrors(
  inputs: CalcInputs, products: ProductProperty[], manual: ManualFlags,
): Record<string, string> {
  const errs = localErrors(inputs, errorContext(inputs, products));
  MANUAL_KEYS.forEach((k) => {
    if (manual[k] && stripOverride(inputs[k]) === "") errs[k] = MAN_EMPTY_MSG;
  });
  return errs;
}

/**
 * Encapsulates all calculator state: reference data bootstrap, debounced
 * auto-refresh, client-side validation and live status. Keeping this in a hook
 * keeps the Calculator component focused on rendering.
 */
export function useCalculator(): UseCalculator {
  const [inputs, setInputs] = useState<CalcInputs>(loadSavedInputs);
  const [manual, setManualState] = useState<ManualFlags>(() => manualFrom(inputs));
  const [notes, setNotes] = useState<Partial<Record<keyof CalcInputs, string>>>({});
  const inputsRef = useRef(inputs);
  inputsRef.current = inputs;
  const manualRef = useRef(manual);
  manualRef.current = manual;
  const [products, setProducts] = useState<ProductProperty[]>([]);
  const [refrigerants, setRefrigerants] = useState<Refrigerant[]>([]);
  const [result, setResult] = useState<CalcResult | null>(null);
  const [vpfDimensions, setVpfDimensions] = useState<VPFDimensions | null>(null);
  const [status, setStatus] = useState<CalcStatus>("idle");
  const [stale, setStale] = useState(false);
  const [banner, setBanner] = useState<string[]>([]);
  const [refDataError, setRefDataError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [loadedDefaults, setLoadedDefaults] = useState(false);
  const reqId = useRef(0);
  // Latest product list for the debounced calc's freezing-point check
  const productsRef = useRef<ProductProperty[]>([]);
  productsRef.current = products;

  // Any edit (value or AUTO/MAN mode) re-arms the debounced calculation, so
  // the "updating" status set below is always followed by a run.
  const trigger = useMemo(() => ({ inputs, manual }), [inputs, manual]);
  const debounced = useDebounce(trigger, 400);

  // ---- bootstrap reference data + defaults (run once) ----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // allSettled: one failing endpoint must not discard the others
        const [prodsR, refsR, cfgR] = await Promise.allSettled([
          fetchProducts(),
          fetchRefrigerants(),
          fetchConfig(),
        ]);
        if (cancelled) return;
        const failed: string[] = [];
        if (prodsR.status === "fulfilled" && Array.isArray(prodsR.value)) setProducts(prodsR.value);
        else failed.push("products");
        if (refsR.status === "fulfilled" && Array.isArray(refsR.value)) setRefrigerants(refsR.value);
        else failed.push("refrigerants");
        const cfg = cfgR.status === "fulfilled" ? cfgR.value : null;
        if (!cfg) failed.push("configuration");
        if (failed.length) {
          [prodsR, refsR, cfgR].forEach((r) => {
            if (r.status === "rejected") console.error("Failed to load reference data:", r.reason);
          });
          setRefDataError(
            `Could not load ${failed.join(", ")} from the server — lists and defaults may be `
            + "incomplete. Check the connection and reload the page.",
          );
        }
        if (cfg && cfg.defaults && !hasStoredInputs) {
          // Only apply API defaults when there are no saved inputs; otherwise
          // the user's persisted state would be overwritten on every page load.
          const d = cfg.defaults;
          setInputs((prev) => {
            const next = { ...prev } as CalcInputs;
            (Object.keys(prev) as (keyof CalcInputs)[]).forEach((k) => {
              if (d[k] !== undefined && d[k] !== null) {
                (next[k] as string) = String(d[k]);
              }
            });
            return next;
          });
          // Keep the AUTO/MAN flags in step with any defaulted override values
          setManualState((prev) => ({
            overrideFreezeTime: prev.overrideFreezeTime
              || (d.overrideFreezeTime != null && String(d.overrideFreezeTime).trim() !== ""),
            overrideDefrostMin: prev.overrideDefrostMin
              || (d.overrideDefrostMin != null && String(d.overrideDefrostMin).trim() !== ""),
          }));
        }
      } catch (err) {
        console.error("Failed to load reference data:", err);
      } finally {
        if (!cancelled) setLoadedDefaults(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ---- immediate status + field-error feedback on edit ----
  // Hard limits + cross-field rules (lib/validators) are checked here, so an
  // out-of-range value blocks the calculation just like a blank/NaN one.
  useEffect(() => {
    if (!loadedDefaults) return;
    const errs = allErrors(inputs, products, manual);
    setFieldErrors(errs);
    // Invalidate any in-flight request in BOTH branches: its response belongs
    // to older inputs and must not land as "ok" during the debounce window
    // (that would enable Save with new inputs paired to old outputs).
    reqId.current += 1;
    if (Object.keys(errs).length) {
      // Grey the results until a valid recalculation succeeds.
      setStatus("invalid");
      setStale(true);
    } else {
      setStatus("updating");
    }
  }, [inputs, products, manual, loadedDefaults]);

  // ---- debounced calculation ----
  const runCalc = useCallback(async (payload: CalcInputs) => {
    if (Object.keys(allErrors(payload, productsRef.current, manualRef.current)).length) {
      reqId.current += 1;   // drop any in-flight response
      setStatus("invalid");
      setStale(true);
      setBanner([]);
      return;
    }
    const myId = reqId.current + 1;
    reqId.current = myId;
    setStatus("updating");
    try {
      // Run the energy/cycle calculation and the VPF geometry build in parallel.
      const [resp, vpfResp] = await Promise.all([
        calculateApi(payload),
        buildVPF(payload).catch(() => null), // VPF failure is non-blocking
      ]);
      if (myId !== reqId.current) return; // superseded by a newer request

      // Main calculation result
      if (resp.ok && resp.result) {
        setResult(resp.result);
        setBanner([]);
        setStatus("ok");
        setStale(false);
        // ── Persist result for Production Gantt sync ────────────────────
        try {
          localStorage.setItem("jackstone-vpf-result-v1", JSON.stringify(resp.result));
        } catch {/* ignore */}
      } else {
        setBanner(resp.errors || ["Calculation failed."]);
        setStatus("invalid");
        setStale(true);
      }

      // VPF geometry dimensions
      if (vpfResp?.ok && vpfResp.result?.vpf) {
        const v = vpfResp.result.vpf;
        setVpfDimensions({
          OALength:        v.OALength,
          OADepth:         v.OADepth,
          OAHeightLowered: v.OAHeightLowered,
          OAHeightRaised:  v.OAHeightRaised,
          InternalVolume:  v.InternalVolume,
          lStackClosed:    v.lStackClosed,
          lStackOpen:      v.lStackOpen,
          mPlates:         v.mPlates,
          pPressure:       v.pPressure,
          modelCode:       v.modelCode,
          valid:           vpfResp.result.valid ?? true,
          errors:          vpfResp.result.errors ?? [],
        });
      } else {
        setVpfDimensions(null);
      }
    } catch (err) {
      if (myId !== reqId.current) return;
      console.error("Calculation request failed:", err);
      // Distinct from "invalid": the inputs are fine, the server is unreachable.
      setBanner([]);
      setStatus("error");
      setStale(true);
    }
  }, []);

  useEffect(() => {
    if (!loadedDefaults) return;
    runCalc(debounced.inputs);
  }, [debounced, loadedDefaults, runCalc]);

  const set = useCallback(
    (key: keyof CalcInputs) => (value: string) => {
      // The blur note stays only until the next edit of that field
      setNotes((prev) => {
        if (!(key in prev)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setInputs((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  /** Called on blur — applies the autocorrect rule (with a note explaining any
   *  change), or keeps a "!" override. */
  const blur = useCallback(
    (key: keyof CalcInputs) => () => {
      const current = inputsRef.current[key] as string;
      const { value, note } = applyOnBlurWithNote(key, current);
      if (value !== current) setInputs((prev) => ({ ...prev, [key]: value }));
      if (note) setNotes((prev) => ({ ...prev, [key]: note }));
    },
    [],
  );

  /** AUTO/MAN pill: switch mode, pre-filling the MAN value when switching on. */
  const setManual = useCallback((key: ManualKey, on: boolean, prefill = "") => {
    setManualState((prev) => ({ ...prev, [key]: on }));
    setInputs((prev) => ({ ...prev, [key]: on ? prefill : "" }));
  }, []);

  /** Replace all inputs at once (used by project load). */
  const resetInputs = useCallback(
    (partial: Partial<CalcInputs>) => {
      const next = { ...FALLBACK_INPUTS, ...partial };
      setInputs(next);
      setManualState(manualFrom(next));
      setNotes({});
    },
    [],
  );

  /** Reset to factory defaults and wipe persisted state. */
  const clearInputs = useCallback(() => {
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
    setInputs(FALLBACK_INPUTS);
    setManualState(manualFrom(FALLBACK_INPUTS));
    setNotes({});
  }, []);

  // ── Persist inputs to localStorage on every change ────────────────────────
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(inputs)); }
    catch { /* storage full or unavailable (private browsing) */ }
  }, [inputs]);

  const productOptions = useMemo<Option[]>(
    () => products.map((product) => ({ value: product.name, label: product.name })),
    [products],
  );
  const primaryRefrigerantOptions = useMemo<Option[]>(
    () => refrigerants
      .filter((r) => !r.refrigerant_type || r.refrigerant_type === "Primary")
      .map((ref) => ({ value: ref.label, label: ref.label })),
    [refrigerants],
  );
  const refrigerantOptions = primaryRefrigerantOptions;

  const heatTransferFluidOptions = useMemo<Option[]>(
    () => refrigerants
      .filter((r) => r.refrigerant_type === "Secondary")
      .map((ref) => ({ value: ref.label, label: ref.label })),
    [refrigerants],
  );

  const htfOutletTemp = useMemo<number | null>(() => {
    const inStr = stripOverride(inputs.htfInletTemp);
    const dtStr = stripOverride(inputs.htfDeltaT);
    // Blank must show "—", not Number("") = 0
    if (inStr === "" || dtStr === "") return null;
    const inlet = Number(inStr);
    const delta = Number(dtStr);
    if (!isFinite(inlet) || !isFinite(delta)) return null;
    return inlet + delta;
  }, [inputs.htfInletTemp, inputs.htfDeltaT]);

  return {
    inputs, set, blur, notes, manual, setManual, resetInputs, clearInputs, products, refrigerants, productOptions,
    refrigerantOptions, primaryRefrigerantOptions, heatTransferFluidOptions,
    htfOutletTemp,
    result, vpfDimensions, status, stale, banner, refDataError, fieldErrors,
  };
}
